import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import type { QueryClient } from '@tanstack/react-query';
import { QueryClientProvider } from '@tanstack/react-query';
import { useEffect, type ReactNode } from 'react';
import { ToastProvider } from './components/common/Toast';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { ThemeProvider } from './features/theme/ThemeProvider';
import { LanguageProvider } from './features/language/LanguageProvider';
import { analytics } from './lib/analytics';
import { AuthLayout } from './components/layout/AuthLayout';
import { DashboardLayout } from './components/layout/DashboardLayout';
import { AdminLayout } from './components/layout/AdminLayout';
import { ProtectedRoute } from './components/layout/ProtectedRoute';
import { LoginPage } from './pages/auth/LoginPage';
import { RegisterPage } from './pages/auth/RegisterPage';
import { VerifyOtpPage } from './pages/auth/VerifyOtpPage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { InventoryPage } from './pages/inventory/InventoryPage';
import { CreateInventoryPage } from './pages/inventory/CreateInventoryPage';
import { WorkOrdersPage } from './pages/work-orders/WorkOrdersPage';
import { CreateWorkOrderPage } from './pages/work-orders/CreateWorkOrderPage';
import { WorkOrderViewPage } from './pages/work-orders/WorkOrderViewPage';
import { RecipesPage } from './pages/recipes/RecipesPage';
import { PurchaseOrderList } from './pages/purchase-orders/PurchaseOrderList';
import { CreatePurchaseOrderPage } from './pages/purchase-orders/CreatePurchaseOrderPage';
import { SupplierList } from './pages/purchasing/SupplierList';
import { LotList } from './pages/purchasing/LotList';
import { SampleList } from './pages/purchasing/SampleList';
import { LotDetailPage } from './pages/inventory/LotDetailPage';
import { FinishedGoodViewPage } from './pages/inventory/FinishedGoodViewPage';
import { GreenBeanProfileList } from './pages/purchasing/GreenBeanProfileList';
import { GreenBeanProfileForm } from './pages/purchasing/GreenBeanProfileForm';
import { SalesOrderList } from './pages/sales-orders/SalesOrderList';
import { CustomerList } from './pages/selling/CustomerList';
import { SettingsPage } from './pages/settings/SettingsPage';
import { MemberSettings } from './pages/settings/MemberSettings';
import { RolesPage } from './pages/settings/RolesPage';
import { BillingSettings } from './pages/settings/BillingSettings';
import { AdminMemberSettings } from './pages/settings/AdminMemberSettings';
import SubscriptionSettings from './pages/settings/SubscriptionSettings';
import VouchersSettings from './pages/settings/VouchersSettings';
import CompaniesSubscriptionsSettings from './pages/settings/CompaniesSubscriptionsSettings';
import LynkPaymentEventsPage from './pages/settings/LynkPaymentEventsPage';
import ChatSupportPage from './pages/admin/chat-support/ChatSupportPage';
import { GeneralReport } from './pages/reports/GeneralReport';
import { ShiftReport } from './pages/reports/ShiftReport';
import { RoastingPage } from './pages/roasts/RoastPage';
import { BatchList } from './pages/batches/BatchList';
import { ArtisanSyncPage } from './pages/artisan-sync/ArtisanSyncPage';
import { MachineList } from './pages/production/MachineList';
import { RoasterList } from './pages/production/RoasterList';
import { CuppingSessionsPage } from './pages/cupping-sessions/CuppingSessionsPage';
import { CuppingSessionFormPage } from './pages/cupping-sessions/CuppingSessionFormPage';
import { QoraAssistantPage } from './pages/qora-assistant/QoraAssistantPage';
import ActivityLogsList from './pages/activity-logs/ActivityLogsList';
import { queryClient } from './lib/queryClient';

// Expose queryClient on window so the axios interceptor can invalidate queries
// (e.g., subscription cache on 402) without needing React context.
if (typeof window !== 'undefined') {
  (window as unknown as { __qc: QueryClient }).__qc = queryClient;
}

function PageTracker() {
  const location = useLocation();
  useEffect(() => {
    analytics.page(location.pathname);
  }, [location.pathname]);
  return null;
}

function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  return <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <LanguageProvider>
          <ThemeProvider>
            <Router>
              <PageTracker />
              <RouteErrorBoundary>
                <Routes>
              {/* Public Routes */}
              <Route element={<AuthLayout />}>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/verify-otp" element={<VerifyOtpPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
              </Route>

              {/* Protected Routes */}
              <Route
                element={
                  <ProtectedRoute>
                    <DashboardLayout />
                  </ProtectedRoute>
                }
              >
                <Route path="/" element={<DashboardPage />} />
                <Route
                  path="/qora-assistant"
                  element={
                    <ProtectedRoute requiredPermission={{ obj: 'qora_assistant', act: 'write' }}>
                      <QoraAssistantPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/inventory/create"
                  element={
                    <ProtectedRoute requiredPermission={{ obj: 'inventory', act: 'write' }}>
                      <CreateInventoryPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/inventory/edit/:id"
                  element={
                    <ProtectedRoute requiredPermission={{ obj: 'inventory', act: 'write' }}>
                      <CreateInventoryPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/inventory"
                  element={
                    <ProtectedRoute requiredPermission={{ obj: 'inventory', act: 'read' }}>
                      <InventoryPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/inventory/lots/:id"
                  element={
                    <ProtectedRoute requiredPermission={{ obj: 'inventory', act: 'read' }}>
                      <LotDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/inventory/finished-goods/:id/view"
                  element={
                    <ProtectedRoute requiredPermission={{ obj: 'inventory', act: 'read' }}>
                      <FinishedGoodViewPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/work-orders"
                  element={
                    <ProtectedRoute
                      requiredAnyOf={[
                        { obj: 'work_orders.roasting', act: 'read' },
                        { obj: 'work_orders.packing', act: 'read' },
                      ]}
                    >
                      <WorkOrdersPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/work-orders/create"
                  element={
                    <ProtectedRoute requiredPermission={{ obj: 'work_orders.roasting', act: 'write' }}>
                      <CreateWorkOrderPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/work-orders/:id/view"
                  element={
                    <ProtectedRoute
                      requiredAnyOf={[
                        { obj: 'work_orders.roasting', act: 'read' },
                        { obj: 'work_orders.packing', act: 'read' },
                      ]}
                    >
                      <WorkOrderViewPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/recipes"
                  element={
                    <ProtectedRoute requiredPermission={{ obj: 'recipes', act: 'read' }}>
                      <RecipesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/roasting"
                  element={
                    <ProtectedRoute requiredPermission={{ obj: 'roast_profiles', act: 'read' }}>
                      <RoastingPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/batches"
                  element={
                    <ProtectedRoute requiredPermission={{ obj: 'work_orders.roasting', act: 'read' }}>
                      <BatchList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/artisan-sync"
                  element={
                    <ProtectedRoute requiredPermission={{ obj: 'artisan_profiles', act: 'read' }}>
                      <ArtisanSyncPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/production/machines"
                  element={
                    <ProtectedRoute requiredPermission={{ obj: 'machines', act: 'read' }}>
                      <MachineList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/production/roasters"
                  element={
                    <ProtectedRoute requiredPermission={{ obj: 'roasters', act: 'read' }}>
                      <RoasterList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/cupping-sessions"
                  element={
                    <ProtectedRoute requiredPermission={{ obj: 'cupping_sessions', act: 'read' }}>
                      <CuppingSessionsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/cupping-sessions/:id"
                  element={
                    <ProtectedRoute requiredPermission={{ obj: 'cupping_sessions', act: 'read' }}>
                      <CuppingSessionFormPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/purchase-orders"
                  element={
                    <ProtectedRoute requiredPermission={{ obj: 'purchase_orders', act: 'read' }}>
                      <PurchaseOrderList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/purchase-orders/create"
                  element={
                    <ProtectedRoute requiredPermission={{ obj: 'purchase_orders', act: 'write' }}>
                      <CreatePurchaseOrderPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/purchasing/suppliers"
                  element={
                    <ProtectedRoute requiredPermission={{ obj: 'suppliers', act: 'read' }}>
                      <SupplierList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/purchasing/lots"
                  element={
                    <ProtectedRoute requiredPermission={{ obj: 'inventory', act: 'read' }}>
                      <LotList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/purchasing/samples"
                  element={
                    <ProtectedRoute requiredPermission={{ obj: 'green_bean_profiles', act: 'read' }}>
                      <SampleList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/purchasing/green-beans"
                  element={
                    <ProtectedRoute requiredPermission={{ obj: 'green_bean_profiles', act: 'read' }}>
                      <GreenBeanProfileList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/purchasing/green-beans/new"
                  element={
                    <ProtectedRoute requiredPermission={{ obj: 'green_bean_profiles', act: 'write' }}>
                      <GreenBeanProfileForm />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/purchasing/green-beans/:id"
                  element={
                    <ProtectedRoute requiredPermission={{ obj: 'green_bean_profiles', act: 'read' }}>
                      <GreenBeanProfileForm />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/purchasing/green-beans/:id/edit"
                  element={
                    <ProtectedRoute requiredPermission={{ obj: 'green_bean_profiles', act: 'write' }}>
                      <GreenBeanProfileForm />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/sales-orders"
                  element={
                    <ProtectedRoute requiredPermission={{ obj: 'sales_orders', act: 'read' }}>
                      <SalesOrderList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/selling/customers"
                  element={
                    <ProtectedRoute requiredPermission={{ obj: 'customers', act: 'read' }}>
                      <CustomerList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports/general"
                  element={
                    <ProtectedRoute
                      requiredAnyOf={[
                        { obj: 'reports.roasting', act: 'read' },
                        { obj: 'reports.green_beans', act: 'read' },
                        { obj: 'reports.roasted_beans', act: 'read' },
                        { obj: 'reports.business', act: 'read' },
                      ]}
                    >
                      <GeneralReport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports/shifts"
                  element={
                    <ProtectedRoute requiredPermission={{ obj: 'reports.business', act: 'read' }}>
                      <ShiftReport />
                    </ProtectedRoute>
                  }
                />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/settings/member" element={<MemberSettings />} />
                <Route
                  path="/settings/roles"
                  element={
                    <ProtectedRoute requiredPermission={{ obj: 'members', act: 'read' }}>
                      <RolesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/activity-logs"
                  element={
                    <ProtectedRoute requiredPermission={{ obj: 'activity_logs', act: 'read' }}>
                      <ActivityLogsList />
                    </ProtectedRoute>
                  }
                />
                <Route path="/settings/billing" element={<BillingSettings />} />
                <Route
                  path="/settings/subscription"
                  element={
                    <ProtectedRoute requiredPermission={{ obj: 'subscription', act: 'read' }}>
                      <SubscriptionSettings />
                    </ProtectedRoute>
                  }
                />

                {/* Legacy redirects — old admin paths now live under /admin/* */}
                <Route path="/settings/admin-member" element={<Navigate to="/admin/members" replace />} />
                <Route path="/settings/vouchers" element={<Navigate to="/admin/vouchers" replace />} />
                <Route
                  path="/settings/companies-subscriptions"
                  element={<Navigate to="/admin/companies-subscriptions" replace />}
                />
              </Route>

              {/* Admin Console — super_admin only, dedicated layout & sidebar */}
              <Route element={<AdminLayout />}>
                <Route path="/admin" element={<Navigate to="/admin/members" replace />} />
                <Route
                  path="/admin/members"
                  element={
                    <ProtectedRoute requiredPermission={{ obj: 'settings.admin_member', act: 'read' }}>
                      <AdminMemberSettings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/vouchers"
                  element={
                    <ProtectedRoute requiredPermission={{ obj: 'admin.vouchers', act: 'read' }}>
                      <VouchersSettings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/companies-subscriptions"
                  element={
                    <ProtectedRoute requiredPermission={{ obj: 'admin.subscriptions', act: 'read' }}>
                      <CompaniesSubscriptionsSettings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/lynk-payment-events"
                  element={
                    <ProtectedRoute requiredPermission={{ obj: 'admin.lynk_payment_events', act: 'read' }}>
                      <LynkPaymentEventsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/chat-support"
                  element={
                    <ProtectedRoute requiredPermission={{ obj: 'chat.support.admin', act: 'read' }}>
                      <ChatSupportPage />
                    </ProtectedRoute>
                  }
                />
              </Route>

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </RouteErrorBoundary>
            </Router>
          </ThemeProvider>
        </LanguageProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}

export default App;
